import React, { useState, useCallback, useRef } from 'react';
import { EARPHONE_DETECTION_CONFIG } from '@/constants/video-call';
import { useDeepgramEarphoneTest } from './useDeepgramEarphoneTest';

interface EarphoneDetectionResult {
  status: 'idle' | 'testing' | 'earphones_detected' | 'speakers_detected' | 'inconclusive' | 'error';
  confidence: number; // 0-1, higher = more confident
  isChecking: boolean;
  error?: string;
  testPhase?: 'baseline' | 'tone_test' | 'analysis' | 'complete' | 'setup' | 'playing_audio' | 'transcribing' | 'analyzing';
  snrGain?: number; // Signal-to-noise ratio gain in dB (legacy echo test)
  echoLevel?: number; // Detected echo level (legacy echo test)
  // New transcription test fields
  capturedTranscript?: string;
  expectedPhrase?: string;
  matchScore?: number;
}

interface EarphoneDetectionConfig {
  testFrequencies: number[]; // Test tone frequencies in Hz
  toneDuration: number; // Duration of each tone in ms
  baselineDuration: number; // Baseline measurement duration in ms
  analysisDuration: number; // Analysis phase duration in ms
  echoThresholdDb: number; // Echo detection threshold in dB
  confidenceThreshold: number; // Minimum confidence for definitive result
}

const DEFAULT_CONFIG: EarphoneDetectionConfig = {
  testFrequencies: EARPHONE_DETECTION_CONFIG.TEST_FREQUENCIES,
  toneDuration: EARPHONE_DETECTION_CONFIG.TONE_DURATION,
  baselineDuration: EARPHONE_DETECTION_CONFIG.BASELINE_DURATION,
  analysisDuration: EARPHONE_DETECTION_CONFIG.ANALYSIS_DURATION,
  echoThresholdDb: EARPHONE_DETECTION_CONFIG.ECHO_THRESHOLD_DB,
  confidenceThreshold: EARPHONE_DETECTION_CONFIG.CONFIDENCE_THRESHOLD,
};

export const useEarphoneDetection = (config: Partial<EarphoneDetectionConfig> = {}) => {
  const [result, setResult] = useState<EarphoneDetectionResult>({
    status: 'idle',
    confidence: 0,
    isChecking: false,
    testPhase: 'setup'
  });

  // Use Deepgram-based transcription test as primary method
  const deepgramTest = useDeepgramEarphoneTest();
  
  const configRef = useRef<EarphoneDetectionConfig>({ ...DEFAULT_CONFIG, ...config });
  const abortControllerRef = useRef<AbortController | null>(null);

  const testEarphoneConnection = useCallback(async (selectedAudioDeviceId?: string, selectedAudioOutputDeviceId?: string): Promise<boolean> => {
    try {
      // Run the Deepgram test - state syncing handled by useEffect
      const result = await deepgramTest.testEarphoneConnection(selectedAudioDeviceId, selectedAudioOutputDeviceId);
      return result;
    } catch (error) {
      console.error('Earphone test failed:', error);
      
      // Only set error state manually if Deepgram test didn't handle it
      if (deepgramTest.result.status !== 'error') {
        const errorResult: EarphoneDetectionResult = {
          status: 'error',
          confidence: 0,
          isChecking: false,
          testPhase: 'complete',
          error: error instanceof Error ? error.message : 'Deepgram test failed'
        };

        setResult(errorResult);
      }
      return false;
    }
  }, [deepgramTest]);

  // Sync state changes from Deepgram test in real-time
  React.useEffect(() => {
    const deepgramResult = deepgramTest.result;
    
    setResult(prevResult => {
      // Only update if there are actual changes to avoid unnecessary re-renders
      if (
        prevResult.status !== deepgramResult.status ||
        prevResult.isChecking !== deepgramResult.isChecking ||
        prevResult.testPhase !== deepgramResult.testPhase ||
        prevResult.confidence !== deepgramResult.confidence ||
        prevResult.error !== deepgramResult.error
      ) {
        return {
          status: deepgramResult.status,
          confidence: deepgramResult.confidence,
          isChecking: deepgramResult.isChecking,
          error: deepgramResult.error,
          testPhase: deepgramResult.testPhase,
          capturedTranscript: deepgramResult.capturedTranscript,
          expectedPhrase: deepgramResult.expectedPhrase,
          matchScore: deepgramResult.matchScore
        };
      }
      return prevResult;
    });
  }, [deepgramTest.result]);

  const resetTest = useCallback(() => {
    // Reset Deepgram test
    deepgramTest.resetTest();
    
    // Reset local state
    setResult({
      status: 'idle',
      confidence: 0,
      isChecking: false,
      testPhase: 'setup'
    });
  }, [deepgramTest]);

  const stopTest = useCallback(() => {
    deepgramTest.stopTest();
  }, [deepgramTest]);

  return { 
    result, 
    testEarphoneConnection, 
    resetTest, 
    stopTest,
    // Expose Deepgram test support status
    isTranscriptionSupported: deepgramTest.isSupported
  };
};